import * as React from 'react';
import FindLocationResultState from '../data/schema/FindLocationResultState';
import resolveSelectedLocation from '../actions/resolveSelectedLocation';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { LocationPersonaControl, LocationPersonaControlViewState } from 'owa-location-persona';
import { LocationWellColor } from 'owa-location-common-well/lib/utils/generateLocationWellStyle';
import { observer } from 'mobx-react';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { RoomIcon } from 'owa-room-control';
import store from 'owa-feedback-manager-store/lib/store/locationFeedbackManagerStore';
import pauseSession from 'owa-feedback-manager-actions/lib/actions/pauseSession';
import shouldRenderCity from 'owa-location-common-well/lib/utils/shouldRenderCity';
import getAvailabilityTextAndStyle from 'owa-location-common-well/lib/utils/getAvailabilityTextAndStyle';

let styles = require('./LocationResult.scss');
let classNames = require('classnames/bind').bind(styles);

export interface LocationResultProps {
    findLocationResultState?: FindLocationResultState;
    locationResult: LocationPersonaControlViewState;
    locationWellColor: LocationWellColor;
    roomFreeBusyEnabled: boolean;
}

/**
 * This the location result control, used to render an entry in the location search drop down
 */
@observer
export default class LocationResult extends React.Component<LocationResultProps, any> {
    private addSelectedLocation = (evt: React.MouseEvent<any>) => {
        const { findLocationResultState, locationResult } = this.props;
        evt.preventDefault();
        resolveSelectedLocation(findLocationResultState, locationResult);
        pauseSession(store.feedbackManagerState);
    };

    render() {
        let { locationResult } = this.props;
        let hasAddress = !isNullOrWhiteSpace(locationResult.displayAddress);
        let isRoom = locationResult.personaType == 'Room';
        let isRoomWithAvailabilityInfo = isRoom && this.props.roomFreeBusyEnabled;

        let hasSecondLine = hasAddress || isRoomWithAvailabilityInfo;

        let iconImage = isRoom ? (
            <RoomIcon backgroundColor={this.props.locationWellColor.rootSecondaryColor} />
        ) : (
            <LocationPersonaControl locationPersona={locationResult} size={PersonaSize.size32} />
        );

        return (
            <div
                className={classNames(styles.container)}
                onMouseUp={this.addSelectedLocation}
                title={locationResult.displayName}>
                <div className={styles.personaImage}>{iconImage}</div>
                <div className={styles.personaText}>
                    <span
                        className={classNames(
                            styles.personaLineOne,
                            { singleLine: !hasSecondLine },
                            styles.personaTextLine
                        )}>
                        {locationResult.displayName}
                    </span>
                    {hasSecondLine &&
                        this.getSecondLineText(locationResult, isRoomWithAvailabilityInfo)}
                </div>
            </div>
        );
    }

    private getSecondLineText(
        locationResult: LocationPersonaControlViewState,
        isRoomWithAvailabilityInfo: boolean
    ): JSX.Element {
        let secondaryLine = (
            <span className={classNames(styles.personaLineTwo, styles.personaTextLine)}>
                {locationResult.displayAddress}
            </span>
        );

        if (isRoomWithAvailabilityInfo) {
            let { freebusyText, freebusyStyle } = getAvailabilityTextAndStyle(
                locationResult,
                styles
            );
            secondaryLine = (
                <div className={classNames(styles.locationSecondRow)}>
                    <Icon
                        iconName={ControlIcons.Clock}
                        className={classNames(styles.icon, freebusyStyle)}
                    />
                    <span className={classNames(styles.roomSecondaryText, freebusyStyle)}>
                        {freebusyText}
                    </span>
                    {shouldRenderCity(locationResult) && (
                        <>
                            <Icon
                                iconName={ControlIcons.Poi}
                                className={styles.roomSecondaryTextIcon}
                            />
                            <span className={styles.roomSecondaryText}>
                                {locationResult.postalAddress.City}
                            </span>
                        </>
                    )}
                </div>
            );
        }

        return secondaryLine;
    }
}
